import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { TaskListRecord, TasksListResponse } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";
import { unwrapGamePassCollection } from "./responseMappers";

/** Scope the tasks list to a season *or* a campaign. The BE reuses the same
 *  `GET /tasks` endpoint for both, keyed on `season_id` / `campaign_id` (see
 *  `ApiRoutes.js`), so exactly one of these should be set. */
export type TasksListQuery = {
  seasonId?: string;
  campaignId?: string;
  enabled?: boolean;
};

const fetchTasksList = async <T extends TaskListRecord = TaskListRecord>({
  seasonId,
  campaignId,
}: TasksListQuery): Promise<TasksListResponse<T>> => {
  const response = await getPrivateInstance().get<TasksListResponse<T>>(
    API_ROUTES.GET_TASKS_LIST,
    {
      params: {
        ...(seasonId ? { season_id: seasonId } : {}),
        ...(campaignId ? { campaign_id: campaignId } : {}),
      },
    },
  );
  return {
    ...response.data,
    tasks: unwrapGamePassCollection<T>(response.data, "tasks"),
  };
};

/**
 * React Query hook bound to `GET /game-pass-admin/v2/admin/tasks?season_id=…`.
 *
 * Returns every task on the season in one envelope; the Season Details
 * editor groups them under the matching challenge card by `challenge_id`
 * (falling back to `challenge` name + `challenge_order` when the id is
 * null). Disabled until a `seasonId` is available so the page doesn't
 * fire a request with an empty param while the route is still resolving.
 *
 * Generic over the task record type: season callers use the default
 * (`TaskListRecord`), campaign callers pass `CampaignTaskListRecord` to read
 * `tiers` / `campaign_id` off `tasks` without an `as` cast.
 */
export const useGetTasksList = <T extends TaskListRecord = TaskListRecord>(
  params?: TasksListQuery,
): UseQueryResult<TasksListResponse<T>, Error> =>
  useQuery<TasksListResponse<T>, Error>({
    queryKey: [
      "game-pass",
      "tasks",
      "list",
      params?.seasonId ?? "",
      params?.campaignId ?? "",
    ],
    queryFn: () =>
      fetchTasksList<T>({
        seasonId: params?.seasonId,
        campaignId: params?.campaignId,
      }),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
    enabled:
      Boolean(params?.seasonId || params?.campaignId) &&
      (params?.enabled ?? true),
  });

export { fetchTasksList };
